import React, { useMemo, useState } from 'react';
import DecisionWidget from './DecisionWidget';

const DEFAULT_API_URL = 'https://mizar-api.vercel.app';

const DEFAULT_REQUEST = {
  origin: 'MAN',
  destination: 'BCN',
  outbound_date: '2026-07-15',
  return_date: '2026-07-22',
  price_gbp: 285.5,
  session_id: 'playground_test',
  client_platform: 'web',
};

function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

function getStatusLabel(status) {
  if (status >= 200 && status < 300) return `${status} OK`;
  if (status === 400) return '400 Bad Request';
  if (status === 401) return '401 Unauthorized';
  if (status === 403) return '403 Forbidden';
  if (status === 404) return '404 Not Found';
  if (status === 429) return '429 Rate Limited';
  if (status >= 500) return `${status} Server Error`;
  return `${status}`;
}

export default function APIPlayground() {
  const [apiUrl, setApiUrl] = useState(DEFAULT_API_URL);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  const [requestText, setRequestText] = useState(prettyJson(DEFAULT_REQUEST));
  const [response, setResponse] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latency, setLatency] = useState(null);
  const [copied, setCopied] = useState(null);

  const parsedRequest = useMemo(() => {
    try {
      return JSON.parse(requestText);
    } catch {
      return null;
    }
  }, [requestText]);

  const jsonError = useMemo(() => {
    try {
      JSON.parse(requestText);
      return null;
    } catch (err) {
      return 'Invalid JSON. Fix the request body before sending.';
    }
  }, [requestText]);

  const copyFeedback = (type) => {
    setCopied(type);
    window.setTimeout(() => setCopied(null), 2000);
  };

  const copySnippet = async (type) => {
    const safeRequest = parsedRequest || DEFAULT_REQUEST;
    const bodyCompact = JSON.stringify(safeRequest);
    const bodyPretty = prettyJson(safeRequest);
    const authKey = apiKey || 'YOUR_API_KEY';

    let snippet = '';

    if (type === 'curl') {
      snippet = `curl -X POST ${apiUrl}/v1/signal \\
  -H "Authorization: Bearer ${authKey}" \\
  -H "Content-Type: application/json" \\
  -d '${bodyCompact}'`;
    }

    if (type === 'js') {
      snippet = `const response = await fetch('${apiUrl}/v1/signal', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer ${authKey}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(${bodyPretty})
});

const data = await response.json();
console.log(data);`;
    }

    if (type === 'python') {
      snippet = `import requests

response = requests.post(
    '${apiUrl}/v1/signal',
    headers={
        'Authorization': 'Bearer ${authKey}',
        'Content-Type': 'application/json'
    },
    json=${bodyPretty}
)

data = response.json()
print(data)`;
    }

    try {
      await navigator.clipboard.writeText(snippet);
      copyFeedback(type);
    } catch {
      setCopied(null);
    }
  };

  const sendRequest = async () => {
    if (!parsedRequest) {
      setError('Invalid JSON. Fix the request body before sending.');
      setResponse(null);
      setResponseText('');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);
    setResponseText('');
    setLatency(null);

    const start = performance.now();

    try {
      const res = await fetch(`${apiUrl}/v1/signal`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parsedRequest),
      });

      const ms = Math.round(performance.now() - start);
      setLatency({
        status: res.status,
        ms,
        ok: res.ok,
        label: getStatusLabel(res.status),
      });

      const rawText = await res.text();

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${rawText}`);
      }

      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch {
        throw new Error('Response was not valid JSON.');
      }

      setResponse(data);
      setResponseText(prettyJson(data));
    } catch (err) {
      setError(err.message || 'Request failed.');
    } finally {
      setLoading(false);
    }
  };

  const canSend = Boolean(apiKey.trim()) && Boolean(parsedRequest) && !loading;

  return (
    <>
      <style>{`
        .playground-container {
          background: var(--bg-glass);
          border: 1px solid var(--border-subtle);
          border-radius: 12px;
          padding: 32px;
          margin-top: 48px;
        }

        .playground-title {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 700;
        }

        .playground-subtitle {
          margin: 0 0 24px 0;
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.5;
        }

        .playground-section {
          margin-bottom: 24px;
        }

        .playground-label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }

        .playground-label {
          display: block;
          font-weight: 600;
        }

        .playground-input,
        .playground-textarea {
          width: 100%;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid var(--border-subtle);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          box-sizing: border-box;
        }

        .playground-input {
          font-family: 'DM Sans', sans-serif;
        }

        .playground-input.mono,
        .playground-textarea,
        .playground-pre,
        .copy-btn,
        .latency-badge {
          font-family: 'Courier New', monospace;
        }

        .playground-input:focus,
        .playground-textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .playground-textarea {
          resize: vertical;
          min-height: 240px;
          line-height: 1.5;
        }

        .playground-textarea.invalid {
          border-color: #EF6B6B;
        }

        .helper-text {
          margin-top: 8px;
          font-size: 13px;
          color: var(--text-muted);
        }

        .helper-text.error {
          color: #EF6B6B;
        }

        .playground-button-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .playground-button {
          padding: 12px 32px;
          border-radius: 8px;
          border: none;
          background: var(--accent-primary);
          color: white;
          font-weight: 600;
          cursor: pointer;
          font-size: 16px;
          transition: opacity 0.2s ease;
        }

        .playground-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .playground-button:disabled {
          background: #666;
          cursor: not-allowed;
        }

        .secondary-btn {
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid var(--border-subtle);
          background: transparent;
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
        }

        .secondary-btn:hover {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .copy-buttons {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          flex-wrap: wrap;
        }

        .copy-btn {
          padding: 6px 14px;
          border-radius: 6px;
          border: 1px solid var(--border-subtle);
          background: transparent;
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .copy-btn:hover {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .copy-btn.copied {
          border-color: #5FD4A0;
          color: #5FD4A0;
        }

        .playground-response {
          margin-top: 24px;
        }

        .latency-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          font-size: 13px;
        }

        .latency-status.success {
          color: #5FD4A0;
          font-weight: 700;
        }

        .latency-status.error {
          color: #EF6B6B;
          font-weight: 700;
        }

        .latency-ms {
          color: var(--text-muted);
        }

        .playground-response-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .playground-response-label.error {
          color: #EF6B6B;
        }

        .playground-pre {
          padding: 16px;
          border-radius: 8px;
          overflow: auto;
          font-size: 14px;
          margin: 0;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .playground-pre.error {
          background: rgba(239, 107, 107, 0.08);
          border: 1px solid rgba(239, 107, 107, 0.45);
          color: #EF6B6B;
        }

        .playground-pre.success {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border-subtle);
          color: var(--text-primary);
        }

        .raw-response-toggle {
          margin-top: 16px;
        }

        .raw-response-toggle summary {
          cursor: pointer;
          color: var(--text-muted);
          font-size: 14px;
          padding: 8px 0;
          user-select: none;
        }

        .raw-response-toggle summary:hover {
          color: var(--text-secondary);
        }

        .raw-response-toggle[open] summary {
          margin-bottom: 12px;
        }

        @media (max-width: 640px) {
          .playground-container {
            padding: 20px;
          }

          .playground-button {
            width: 100%;
          }

          .playground-button-row {
            align-items: stretch;
          }
        }
      `}</style>

      <div className="playground-container">
        <h3 className="playground-title">API Playground</h3>
        <p className="playground-subtitle">
          Test a live RegretRisk prediction using your API key.
        </p>

        <div className="playground-section">
          <label className="playground-label">API URL</label>
          <input
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            className="playground-input mono"
            placeholder={DEFAULT_API_URL}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="playground-section">
          <div className="playground-label-row">
            <label className="playground-label">API Key</label>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => setShowApiKey((prev) => !prev)}
            >
              {showApiKey ? 'Hide' : 'Show'}
            </button>
          </div>

          <input
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="playground-input mono"
            placeholder="Enter your API key"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="playground-section">
          <label className="playground-label">Request Body</label>
          <textarea
            value={requestText}
            onChange={(e) => setRequestText(e.target.value)}
            rows={10}
            className={`playground-textarea${jsonError ? ' invalid' : ''}`}
            spellCheck={false}
          />

          <div className={`helper-text${jsonError ? ' error' : ''}`}>
            {jsonError || 'Edit the JSON directly to test different routes, dates, prices, and edge cases.'}
          </div>

          <div className="copy-buttons">
            <button
              type="button"
              className={`copy-btn${copied === 'curl' ? ' copied' : ''}`}
              onClick={() => copySnippet('curl')}
            >
              {copied === 'curl' ? '✓ Copied' : 'Copy cURL'}
            </button>

            <button
              type="button"
              className={`copy-btn${copied === 'js' ? ' copied' : ''}`}
              onClick={() => copySnippet('js')}
            >
              {copied === 'js' ? '✓ Copied' : 'Copy JavaScript'}
            </button>

            <button
              type="button"
              className={`copy-btn${copied === 'python' ? ' copied' : ''}`}
              onClick={() => copySnippet('python')}
            >
              {copied === 'python' ? '✓ Copied' : 'Copy Python'}
            </button>
          </div>
        </div>

        <div className="playground-button-row">
          <button
            type="button"
            onClick={sendRequest}
            disabled={!canSend}
            className="playground-button"
          >
            {loading ? 'Sending...' : 'Send Request'}
          </button>
        </div>

        {response && (
          <div className="playground-response">
            {latency && (
              <div className="latency-badge">
                <span className={`latency-status ${latency.ok ? 'success' : 'error'}`}>
                  {latency.label}
                </span>
                <span className="latency-ms">• {latency.ms}ms</span>
              </div>
            )}

            <DecisionWidget prediction={response} />

            <details className="raw-response-toggle">
              <summary>Show raw response</summary>
              <pre className="playground-pre success">{responseText}</pre>
            </details>
          </div>
        )}

        {error && (
          <div className="playground-response">
            {latency && (
              <div className="latency-badge">
                <span className={`latency-status ${latency.ok ? 'success' : 'error'}`}>
                  {latency.label}
                </span>
                <span className="latency-ms">• {latency.ms}ms</span>
              </div>
            )}

            <label className="playground-response-label error">Error</label>
            <pre className="playground-pre error">{error}</pre>
          </div>
        )}
      </div>
    </>
  );
}